/**
 * i18next setup for the Control Center. Vietnamese is the default; English is the
 * source language (every Vietnamese file must translate exactly the English keys,
 * checked by `Translation<T>` at compile time).
 *
 * The chosen language is remembered per browser (localStorage `cc.lang`) and set on
 * `<html lang>`. Timestamps stay UTC in both languages; numbers use the locale's separators.
 */

import i18n from 'i18next';
import { initReactI18next, useTranslation } from 'react-i18next';
import * as en from './locales/en';
import * as vi from './locales/vi';

export const LANGUAGES = ['vi', 'en'] as const;
export type Language = (typeof LANGUAGES)[number];
export const DEFAULT_LANGUAGE: Language = 'vi';
const STORAGE_KEY = 'cc.lang';

export const resources = { en, vi } as const;
export const NAMESPACES = Object.keys(en) as (keyof typeof en)[];

function storedLanguage(): Language {
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    return (LANGUAGES as readonly string[]).includes(v ?? '') ? (v as Language) : DEFAULT_LANGUAGE;
  } catch {
    return DEFAULT_LANGUAGE;
  }
}

void i18n.use(initReactI18next).init({
  resources,
  lng: storedLanguage(),
  fallbackLng: 'en',
  ns: NAMESPACES,
  defaultNS: 'common',
  // Nav item keys are paths ("/ops/overview"); group ids are keyed with "_" instead of ".".
  keySeparator: '.',
  nsSeparator: ':',
  interpolation: { escapeValue: false },
  returnNull: false,
});

document.documentElement.lang = i18n.language;

export function setLanguage(lang: Language): void {
  void i18n.changeLanguage(lang);
  document.documentElement.lang = lang;
  try {
    window.localStorage.setItem(STORAGE_KEY, lang);
  } catch {
    // Storage may be unavailable (private mode); the choice then lasts for this page only.
  }
}

export function currentLanguage(): Language {
  return (LANGUAGES as readonly string[]).includes(i18n.language) ? (i18n.language as Language) : DEFAULT_LANGUAGE;
}

/** BCP 47 locale for Intl number and relative-time formatting. */
export function currentLocale(): string {
  return currentLanguage() === 'vi' ? 'vi-VN' : 'en';
}

/** The active language, re-rendering the caller when it changes. */
export function useLanguage(): Language {
  const { i18n: instance } = useTranslation();
  return (LANGUAGES as readonly string[]).includes(instance.language) ? (instance.language as Language) : DEFAULT_LANGUAGE;
}

export { useTranslation };
export default i18n;
